import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Sparkles, Shield, TrendingUp, Building2, CheckCircle, Star, Zap } from 'lucide-react';
import { getWhatsAppUrl, WhatsAppIcon } from '../../utils/whatsapp';

const floatingCards = [
  { label: 'GST Filed', value: '₹2.4L', color: 'from-green-500 to-emerald-600', delay: 0, x: '4%', y: '18%' },
  { label: 'Loan Approved', value: '₹15L', color: 'from-blue-500 to-cyan-500', delay: 0.3, x: '75%', y: '25%' },
  { label: 'CIBIL Score', value: '782', color: 'from-purple-500 to-violet-600', delay: 0.6, x: '80%', y: '65%' },
  { label: 'Businesses', value: '10K+', color: 'from-orange-500 to-amber-500', delay: 0.9, x: '8%', y: '62%' },
];

export function Hero() {
  const isAuthenticated = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white dark:bg-[#0A1628]">
      {/* Animated background mesh */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-cyan-500/5 dark:bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-blue-500/5 dark:bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-gradient-to-r from-cyan-500/3 to-blue-500/3 dark:from-cyan-500/5 dark:to-blue-500/5 rounded-full blur-3xl" />
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      </div>

      {/* Floating notification cards — desktop only */}
      <div className="hidden lg:block">
        {floatingCards.map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.8, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
            transition={{ opacity: { delay: 0.8 + card.delay, duration: 0.5 }, scale: { delay: 0.8 + card.delay, duration: 0.5 }, y: { delay: 1.3 + card.delay, duration: 3, repeat: Infinity, ease: 'easeInOut' } }}
            className="absolute z-20"
            style={{ left: card.x, top: card.y }}
          >
            <div className="bg-white/90 dark:bg-[#1A2A4A]/90 backdrop-blur-xl border border-slate-200/60 dark:border-[#2A3F5F]/60 rounded-2xl px-4 py-3 shadow-xl shadow-black/5 dark:shadow-black/20 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-lg`}>
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">{card.label}</div>
                <div className="text-lg font-black text-slate-900 dark:text-white">{card.value}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="w-full min-w-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-32">
        <div className="text-center">
          {/* Badge */}
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-sm font-bold shadow-sm">
              <Sparkles className="w-4 h-4" />
              India's #1 MSME Platform — Trusted by 10,000+ Businesses
            </span>
          </div>

          {/* Heading */}
          <h1 className="text-5xl md:text-7xl font-black text-slate-900 dark:text-white leading-[1.1] tracking-tight">
            Start, Manage &<br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent tracking-wide">
              Scale Your Business
            </span>
          </h1>

          {/* Subtext */}
          <p className="mt-6 text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto font-medium leading-relaxed">
            Company registration, GST compliance, MSME loans, credit repair, and digital marketing —
            <span className="text-slate-900 dark:text-white font-bold"> everything your business needs, under one roof</span>.
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={getWhatsAppUrl("Hi! I want to get started with MSMECenter services.")}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="group px-8 py-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-2xl shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/30 hover:scale-[1.02] transition-all duration-300 flex items-center gap-2.5 text-lg">
                <WhatsAppIcon className="w-6 h-6 fill-current" />
                Chat on WhatsApp
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </a>
            <Link to="/services">
              <button className="px-8 py-4 bg-slate-50 dark:bg-[#1A2A4A] text-slate-700 dark:text-slate-300 font-bold rounded-2xl border border-slate-200 dark:border-[#2A3F5F] hover:border-cyan-400/50 hover:scale-[1.02] transition-all duration-300 flex items-center gap-2 text-lg">
                Explore 35+ Services
                <ChevronRight className="w-5 h-5" />
              </button>
            </Link>
          </div>

          {/* Trust Indicators Row */}
          <div className="mt-14 flex flex-wrap items-center justify-center gap-4 md:gap-6">
            {[
              { icon: Shield, text: 'Govt. Recognized', color: 'text-green-500' },
              { icon: Building2, text: 'CA/CS Verified', color: 'text-blue-500' },
              { icon: TrendingUp, text: '10,000+ Businesses', color: 'text-cyan-500' },
              { icon: Star, text: '4.9★ Rated', color: 'text-amber-500' },
              { icon: Zap, text: '22+ Free Tools', color: 'text-purple-500' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2 text-slate-500 dark:text-slate-400 bg-slate-50/80 dark:bg-[#111C2F]/80 backdrop-blur-sm px-3.5 py-2 rounded-xl border border-slate-200/60 dark:border-[#1E2A3A]/60">
                <item.icon className={`w-4 h-4 ${item.color}`} />
                <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{item.text}</span>
              </div>
            ))}
          </div>

          {/* Scrolling marquee of service tags */}
          <div className="mt-10 overflow-hidden">
            <div className="flex animate-marquee gap-3 whitespace-nowrap">
              {[
                'Pvt Ltd Registration', 'GST Filing', 'Udyam', 'Trademark', 'FSSAI License',
                'ISO Certification', 'Business Loans', 'CIBIL Repair', 'EMI Calculator',
                'Invoice Generator', 'ROC Compliance', 'Patent Filing', 'Virtual CFO',
                'Digital Marketing', 'PF & ESI', 'Import Export Code',
                'Pvt Ltd Registration', 'GST Filing', 'Udyam', 'Trademark', 'FSSAI License',
                'ISO Certification', 'Business Loans', 'CIBIL Repair', 'EMI Calculator',
                'Invoice Generator', 'ROC Compliance', 'Patent Filing', 'Virtual CFO',
                'Digital Marketing', 'PF & ESI', 'Import Export Code',
              ].map((tag, i) => (
                <span key={i} className="inline-block px-3 py-1.5 bg-slate-100/80 dark:bg-[#1A2A4A]/60 text-slate-500 dark:text-slate-400 text-[11px] font-bold rounded-lg border border-slate-200/50 dark:border-[#2A3F5F]/50">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
